import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppDispatch, useAppSelector } from '../../store';
import Loader from '../../components/Loader';
import { DateFormat } from '../../utils/formatTime';
import Iconify from '../../components/Iconify';
import { findAgentTransferLogs } from '../../store/agentTransferLogs/api/index';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../store/agentTransferLogs/agentTransferLogsSlice';

const AgentTransferLogs = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.agentTransferLogs);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(
      findAgentTransferLogs({
        limit,
        skip,
        ...(search && { search: search }),
      })
    );
  }, [dispatch, limit, skip, search]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    dispatch(setLimit({ limit: parseInt(event.target.value, 10) }));
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  return (
    <Page title={'Agent Transfer Logs'}>
      <Container maxWidth={'xl'}>
        <Card>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            sx={{ p: 2 }}
          >
            <Box sx={{ width: '100%', maxWidth: 400 }}>
              <TextField
                fullWidth
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by agent name or email..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Stack>

          {isLoading ? (
            <Box sx={{ py: 5 }}>
              <Loader />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
                  <Table sx={{ minWidth: 1200 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Agent Name</TableCell>
                        <TableCell>Agent Email</TableCell>
                        <TableCell>Ads Transferred</TableCell>
                        <TableCell>Credits Transferred</TableCell>
                        <TableCell>Agent Credits Before</TableCell>
                        <TableCell>Agent Credits After</TableCell>
                        <TableCell>Company Credits Before</TableCell>
                        <TableCell>Company Credits After</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Transferred By</TableCell>
                        <TableCell>Transferred At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data && data.length > 0 ? (
                        data.map((row: any) => (
                          <TableRow hover key={row.id}>
                            <TableCell>{row.agent?.name || 'N/A'}</TableCell>
                            <TableCell>{row.agent?.email || 'N/A'}</TableCell>
                            <TableCell align="center">{row.adsTransferredCount}</TableCell>
                            <TableCell align="center">{row.creditsTransferred}</TableCell>
                            <TableCell align="center">{row.agentCreditsBefore}</TableCell>
                            <TableCell align="center">{row.agentCreditsAfter}</TableCell>
                            <TableCell align="center">{row.companyCreditsBefore || 'N/A'}</TableCell>
                            <TableCell align="center">{row.companyCreditsAfter || 'N/A'}</TableCell>
                            <TableCell>{row.reason || '-'}</TableCell>
                            <TableCell>{row.transferredByUser?.name || 'N/A'}</TableCell>
                            <TableCell>{DateFormat(row.transferredAt)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={11} align="center">
                            No data found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
              </TableContainer>

              <Box sx={{ position: 'relative', display: 'flex' }}>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={limit}
                  page={currentPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
};

export default AgentTransferLogs;

